import { useMutation } from "@tanstack/react-query";
import { useForm, type FieldPath } from "react-hook-form";
import { useScreenTitle } from "@/app/layouts/document-title";
import { useAuth } from "@/app/providers/AuthProvider";
import { Banner } from "@altek/ui/components/feedback/Banner";
import { Button } from "@altek/ui/components/ui/Button";
import { sessionApi } from "@/features/auth/api/session.api";
import { PasswordField } from "@/features/auth/components/PasswordField";
import {
  changePasswordSchema,
  isWrongCurrentPassword,
  type ChangePasswordValues,
} from "@/features/auth/components/PasswordChange.schema";
import { authFailureMessage } from "@/features/auth/lib/auth-failure";
import { fieldErrorsFrom } from "@altek/ui/lib/problem";
import { supportCodeOf } from "@/lib/api/support-code";
import { zodResolver } from "@/lib/forms/zod-resolver";
import { toast } from "@altek/ui/lib/utils/toast";

/**
 * Ortak kural (`PasswordChange.schema.ts`); bu ekranın cümleleri «size verilen parola» diyor: kişi parolayı yöneticiden
 * aldı.
 */
const schema = changePasswordSchema;
type Values = ChangePasswordValues;

/**
 * İLK GİRİŞTE PAROLA DEĞİŞTİRME — 31 Ekim 2026, kullanıcı: «bunu yapınca o kullanıcı giriş yapınca default
 * şifresini değiştirmesini isteyecek sistem».
 *
 * Uygulamanın YERİNE çiziliyor (`AppLayout`, `BlockingScreen`; müşteri seçicisiyle aynı yer): parolası
 * başkası tarafından seçilmiş bir hesap onu değiştirmeden hiçbir ekrana geçemiyor, sunucu da başka hiçbir
 * ucu açmıyor. İki alan: verilen parola (kişinin elinde, eskisini bilmek kuralı) ve yenisi. Kaydedince
 * öteki cihazlardaki oturumlar kapanıyor, bu oturum sürüyor; oturum yeniden okununca uygulama açılıyor.
 * Çıkış düğmesi kartın altında (`BlockingScreen`).
 */
export function PasswordChangeRequired() {
  useScreenTitle("Parolanızı değiştirin");
  const { refresh } = useAuth();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "" },
  });

  const change = useMutation({
    mutationFn: (values: Values) =>
      sessionApi.changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword }),
    onSuccess: async () => {
      toast.success("Parolanız değişti", "Bundan sonra yeni parolanızla giriş yapacaksınız.");
      // Bayrak indi: oturum yeniden okununca kabuk bu ekranın yerine uygulamayı çiziyor.
      await refresh();
    },
    onError: (error) => {
      // Yanlış yazılan verilen parola bir oturum olayı değil (`client.ts`): alanın altında, kendi cümlesiyle.
      if (isWrongCurrentPassword(error)) {
        form.setError("currentPassword", { type: "server", message: "Size verilen parola doğru değil." });
        return;
      }
      for (const [field, message] of Object.entries(fieldErrorsFrom(error))) {
        form.setError(field as FieldPath<Values>, { type: "server", message });
      }
    },
  });

  const submit = form.handleSubmit((values) => {
    if (!change.isPending) change.mutate(values);
  });
  const errors = form.formState.errors;
  const rootError =
    change.error && !isWrongCurrentPassword(change.error) && Object.keys(fieldErrorsFrom(change.error)).length === 0
      ? change.error
      : null;

  return (
    <div className="grid min-h-dvh place-items-center bg-bg-app p-6">
      <form
        noValidate
        onSubmit={(event) => {
          void submit(event);
        }}
        className="w-full max-w-96 space-y-4 rounded-lg border border-border bg-surface p-6 shadow-e1"
      >
        <div>
          <h1 className="text-lg font-semibold text-fg">Parolanızı değiştirin</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Size bir yönetici tarafından verilen parolayla girdiniz. Devam etmeden önce kendi parolanızı
            belirleyin; verilen parola bundan sonra çalışmaz.
          </p>
        </div>

        {rootError ? (
          <Banner tone="danger" title="Parola değiştirilemedi" requestId={supportCodeOf(rootError)}>
            {authFailureMessage(rootError)}
          </Banner>
        ) : null}

        <PasswordField
          label="Size verilen parola"
          autoComplete="current-password"
          required
          autoFocus
          error={errors.currentPassword?.message}
          locked={change.isPending}
          {...form.register("currentPassword")}
        />
        <PasswordField
          label="Yeni parola"
          autoComplete="new-password"
          required
          hint="En az 12 karakter ve en az iki farklı karakter türü: küçük harf, büyük harf, rakam, sembol."
          error={errors.newPassword?.message}
          locked={change.isPending}
          {...form.register("newPassword")}
        />

        <Button type="submit" block size="touch" loading={change.isPending}>
          Parolayı değiştir ve devam et
        </Button>
        <p className="text-center text-2xs text-fg-subtle">
          Öteki cihazlardaki açık oturumlarınız kapanır; bu oturum sürer.
        </p>
      </form>
    </div>
  );
}
